import { VFC, useState, useEffect, useContext } from "react";
import {
  Grid,
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Autocomplete,
  FormHelperText,
  Typography,
  Switch,
  IconButton,
  SelectChangeEvent,
} from "@mui/material";
import ButtonArea from "../common/ButtonArea";
import { AlertDialog } from "../common/dialog/AlertDialog";
import { FormLabel } from "../common/form/FormLabel";
import { FormPullDown, PullDownItem } from "../common/form/FormPullDown";

import {
  TerminalEditFormSchema,
  TerminalEditForm,
} from "./api/TerminalForm";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TerminalService } from "./api/TerminalService";
import { AxiosResponse } from "axios";
import { ResultResponse, ResultDialog } from "../common/dialog/ResultDialog";
import { Utility } from "../../utils/Utility";
import Constants from "../../utils/Constants";
import { Tag } from "../../entity/Tag";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../UserProvider";
// import TenantDiagramDialog from "../tenant/TenantDiagramDialog";
import { AccountTree } from "@mui/icons-material";
import { ObjType } from "../../entity/BaseEntity";
import { useCookies } from "react-cookie";
import { Loading } from "../common/Loading";

/**
 * 端末編集画面
 */
const TerminalEdit: VFC = () => {
  const navigate = useNavigate();
  const PAGE_NAME = "端末編集";

  /***************************************************************
   * State管理
   **************************************************************/
  // 端末ID
  const [deviceId] = useState(sessionStorage.getItem("DEVICE_ID")?.split(",") ?? []);

  // 設定する端末情報
  const [device, setDevice] = useState(new TerminalEditForm());

  // 取得した端末情報のリスト
  const [deviceList, setDeviceList] = useState<any>();

  // 利用者選択肢
  const [deviceUserOption, setDeviceUserOption] = useState<PullDownItem[]>();

  // タグ選択肢
  const [tagOption, setTagOption] = useState<PullDownItem[]>();

  // テナント選択肢
  const [tenantOption, setTenantOption] = useState<PullDownItem[]>([]);

  // 組織図ダイアログ
  const [isDiagramDialogOpen, setIsDiagramDialogOpen] = useState(false);

  // 編集確認ダイアログ
  const [isEditAlertDialogOpen, setIsEditAlertDialogOpen] = useState(false);

  // 削除確認ダイアログ
  const [isDeleteAlertDialogOpen, setIsDeleteAlertDialogOpen] = useState(false);

  // 共通ダイアログ
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // 各種処理完了後、画面遷移
  const [movePage, setMovePage] = useState<any>("");

  // 処理結果
  const [resultResponse, setResultResponse] = useState<ResultResponse>({
    status: "",
    code: "",
    displayMessage: "",
    result: [],
    errorMessages: [],
  });

  // 権限リスト
  const [{ authList }] = useCookies(["authList"]);
  const authSet = {
    'ALL_NONE_SUB-TENANT': false,
  };
  authList.forEach((auth: keyof typeof authSet) => { authSet[auth] = true });

  // キャッシュ
  const cache = useContext(UserContext);

  /***************************************************************
   * 共通処理
   **************************************************************/
  // 処理結果ダイアログ(CLOSE)
  const handleResultDialogClose = () => {
    setIsDialogOpen(false);

    if ("" !== movePage) {
      navigate(movePage, { replace: true });
    }
  };
  // バックエンドからのメッセージを設定
  const setBackendValidMessage = (response: any) => {
    let error = response;
    if (error.deviceUserId) {
      setError("deviceUserId", { message: error.deviceUserId });
    }
    if (error.orgId) {
      setError("orgId", { message: error.orgId });
    }
    if (error.isMdmManaged) {
      setError("isMdmManaged", { message: error.isMdmManaged });
    }
    if (error.comment) {
      setError("comment", { message: error.comment });
    }
  };
  // 処理結果を設定
  const setStateResultResponse = (
    status: string,
    code: string,
    displayMessage: string,
    result: string
  ) => {
    setIsDialogOpen(true);
    setResultResponse({
      status,
      code,
      displayMessage,
      result: [result],
      errorMessages: [],
    });
  };

  // プルダウンリストのkeyから値への変換
  const valueFromKey = (option: PullDownItem[], key: string) => {
    for (const item of option) {
      if (item.key === key) {
        return item.value;
      }
    }
    return key;
  };

  /***************************************************************
   * 初期表示処理
   **************************************************************/
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      await Promise.all([
        // 利用者選択肢
        // setDeviceUser(),
        // // タグ選択肢
        // setTag(),
        // // テナント選択肢
        // setTenant(),
      ]);

      if (deviceId.length > 0) {
        // 端末情報取得
        findByEditId();
      }
    })();
  }, []);

  // 利用者選択肢設定
  // const setDeviceUser = async () => {
  //   const service = new TerminalService();
  //   const res = await service.getDeviceUserOption();
  //   setDeviceUserOption(res);
  // };

  // タグ選択肢設定
  // const setTag = async () => {
  //   const { response, option } = await Utility.getTagOption();
  //   if (Constants.ERR_STATUS_NG === response.data.status) {
  //     setIsDialogOpen(true);
  //     setResultResponse(response.data);
  //   }
  //   setTagOption(option);
  // };

  // テナント選択肢設定
  // const setTenant = async () => {
  //   const { response, option } = await Utility.getTenantOption();
  //   if (Constants.ERR_STATUS_NG === response.data.status) {
  //     setIsDialogOpen(true);
  //     setResultResponse(response.data);
  //   }
  //   setTenantOption(option);
  // };

  // 一括編集時のMDM選択肢設定
  const isMdmOption = () => {
    const option: PullDownItem[] = [];
    const optionMap: ObjType = { true: "全て有効", false: "全て無効" };

    Object.entries(optionMap).forEach(([key, value]) => {
      let item: PullDownItem = {
        key,
        value,
      };
      option.push(item);
    });
    return option;
  }

  // タグの編集確認選択肢
  const tagModeOption = () => {
    const option: PullDownItem[] = [];
    const optionMap: ObjType = { "0": "追加", "1": "上書き" };

    Object.entries(optionMap).forEach(([key, value]) => {
      let item: PullDownItem = {
        key,
        value,
      };
      option.push(item);
    });

    return option;
  }

  // コメントの編集確認選択肢
  const commentModeOption = () => {
    const option: PullDownItem[] = [];
    const optionMap: ObjType = { "0": "変更しない", "1": "上書き" };

    Object.entries(optionMap).forEach(([key, value]) => {
      let item: PullDownItem = {
        key,
        value,
      };
      option.push(item);
    });

    return option;
  }

  // 端末情報取得API
  const findByEditId = () => {
    setLoading(true);
    const service = new TerminalService();
    service
      .findByEditId(deviceId)
      .then((response: AxiosResponse) => {
        if (Constants.ERR_STATUS_NG === response.data.status) {
          setIsDialogOpen(true);
          setResultResponse(response.data);
        }
        let deviceList = response.data.data;

        // 一括編集の場合、サーバから null が返される
        if (deviceList.length == 1) {
          setValue("imei", deviceList[0].imei);
          setValue("tenantId", deviceList[0].tenantId);
          // setValue("deviceUserId", deviceList[0].deviceUserId);
          setValue("comment", deviceList[0].comment);
          const option: PullDownItem[] = deviceList[0].tag.map((tag: Tag) => {
            return {
              key: "" + tag.id,
              value: tag.tagName,
            };
          });
          setValue("selectTag", option);
          setValue("isMdmManaged", deviceList[0].isMdmManaged);

          setDevice(deviceList[0]);
          setDeviceList(deviceList);
        } else if (1 < deviceId.length) {
          // 一括編集の場合
          const tempDevice = new TerminalEditForm();
          setValue("commentMode", "0"); // デフォルト変更しないモード
          setValue("tagMode", "0"); // デフォルト追加モード
          tempDevice.imei = "複数選択済み";
          // if (!authSet["ALL_NONE_SUB-TENANT"]) {
          //   tempDevice.tenantId = "複数選択済み";
          // }
          tempDevice.modelName = null as any;
          setDevice(tempDevice);
          setDeviceList(deviceList);
        }
      })
      .catch((err) => { })
      .finally(() => {
        setLoading(false);
      });
  };

  /***************************************************************
   * 編集処理
   **************************************************************/
  const handleEditAlertOpen = () => {
    setIsEditAlertDialogOpen(true);
  };
  // 編集確認ダイアログ(CLOSE)
  const handleEditAlertClose = () => {
    setIsEditAlertDialogOpen(false);
  };
  // 保存ボタン押下(入力チェック)
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    setError,
    control,
    watch,
    formState: { errors },
  } = useForm<TerminalEditForm>({
    resolver: yupResolver(TerminalEditFormSchema),
    reValidateMode: "onSubmit",
    defaultValues: new TerminalEditForm(),
  });

  // 端末情報編集確認API
  const handleEditBtn: SubmitHandler<TerminalEditForm> = (hookForm) => {
    const service = new TerminalService();

    // 他テナント端末編集時の挙動調整
    if (!canEditParameterByOtherTenant()) {
      hookForm.deviceUserId = "";
      hookForm.selectTag = [];
      hookForm.comment = "";
    }

    // 端末情報編集確認API
    service
      .updateConfirm(hookForm)
      .then((response) => {
        if (Constants.ERR_STATUS_OK === response.data.status) {
          // OK
          handleEditAlertOpen();
        } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
          // 入力違反
          setBackendValidMessage(response.data.errorMessages);
        } else {
          // NG
          setIsDialogOpen(true);
          setResultResponse(response.data);
        }
      })
      .catch((err) => {
        setStateResultResponse(
          Constants.ERR_STATUS_NG,
          err.response.status,
          err.response.statusText,
          Constants.E_MSG_SYSTEM_ERR
        );
      })
      .finally(() => {
        // TODO 必要ならローディング
      });
  };
  // 端末情報編集API
  const handleEditConfirmBtn = () => {
    const service = new TerminalService();
    const terminalEditForm = getValues();

    handleEditAlertClose();

    // 端末情報編集API
    service
      .update(terminalEditForm)
      .then((response) => {
        if (Constants.ERR_STATUS_OK === response.data.status) {
          // OK
          setIsDialogOpen(true);
          setResultResponse(response.data);
          // 一覧画面に戻った時、前回の検索条件・ソート順・ページ番号を継承してリストを再読込みする。
          cache.isFlushView = true;
          setMovePage(-1);
        } else if (Constants.ERR_STATUS_INVALID === response.data.status) {
          // 入力違反
          setBackendValidMessage(response.data.errorMessages);
        } else {
          // NG
          setIsDialogOpen(true);
          setResultResponse(response.data);
        }
      })
      .catch((err) => {
        setStateResultResponse(
          Constants.ERR_STATUS_NG,
          err.response.status,
          err.response.statusText,
          Constants.E_MSG_SYSTEM_ERR
        );
      })
      .finally(() => {
        // TODO 必要ならローディング
      });
  };

  const handleBatchEdit = (e: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent) => {
    if (e.target.value === "") {
      setValue("isMdmManaged", undefined);
    } else {
      setValue("isMdmManaged", (e.target.value === "true") ? true : false);
    }
  }

  /***************************************************************
   * 削除処理
   **************************************************************/
  // 削除確認ダイアログ(OPEN)
  const handleDeleteAlertOpen = () => {
    setIsDeleteAlertDialogOpen(true);
  };
  // 削除確認ダイアログ(CLOSE)
  const handleDeleteAlertClose = () => {
    setIsDeleteAlertDialogOpen(false);
  };
  // 削除ボタン押下
  const handleDeleteBtn = () => {
    const service = new TerminalService();

    if (deviceId.length > 0) {
      setIsDeleteAlertDialogOpen(false);
      service
        .delete(deviceId)
        .then((response) => {
          if ("OK" === response.data.status) {
            setIsDialogOpen(true);
            setResultResponse(response.data);
            // 一覧画面に戻った時、前回の検索条件・ソート順・ページ番号を継承してリストを再読込みする。
            // cache.isFlushView = true;
            setMovePage("/terminal_list");
          } else {
            setIsDialogOpen(true);
            setResultResponse(response.data);
          }
        })
        .catch((err) => {
          setStateResultResponse(
            Constants.ERR_STATUS_NG,
            err.response.status,
            err.response.statusText,
            Constants.E_MSG_SYSTEM_ERR
          );
        })
        .finally(() => {
          // TODO 必要ならローディング
        });
    }
  };

  /***************************************************************
   * 組織図処理
   **************************************************************/

  const handleDiagramDialogOpen = () => {
    setIsDiagramDialogOpen(true);
  };

  const handleDiagramDialogClose = () => {
    setIsDiagramDialogOpen(false);
  };

  /***************************************************************
   * その他処理
   **************************************************************/
  // 他テナント端末が選択されているかの判定　trueで編集可能
  const canEditParameterByOtherTenant = () => {
    return (isSelectedMultiDevice() && !isSelectedOtherTenantDeviceMulti() && watch("tenantId") === "") // 複数端末選択時に元の端末情報が自テナントでテナントの変更がなしの場合 
      || (isSelectedMultiDevice() && !isSelectedOtherTenantDeviceMulti() && watch("tenantId") === tenantOption[0]?.key) // 複数端末選択時元の端末情報が自テナントで自テナントを指定している場合
      || (!isSelectedMultiDevice() && watch("tenantId") === "" && device.tenantId === tenantOption[0]?.key) // 単体の場合に元の端末情報が自テナントでテナントの変更なしの場合
      || (!isSelectedMultiDevice() && watch("tenantId") === tenantOption[0]?.key && device.tenantId === tenantOption[0]?.key); // 単体の場合に元の端末情報が自テナントで自テナントを指定している場合
  }

  // 複数端末が設定されているかの判定 trueで複数端末が設定されている
  const isSelectedMultiDevice = () => {
    return deviceId.length > 1;
  }

  // チェックが入っている端末のテナントで他テナントが指定されているかの確認 trueで他テナントが選択されている
  const isSelectedOtherTenantDeviceMulti = () => {
    if (!!tenantOption && !!deviceList) {
      const selectedTenantType = new Set(deviceList.map((d: any) => d.tenantId.toString()));
      return 1 < selectedTenantType.size || !selectedTenantType.has(tenantOption[0].key.toString());
    }

    return false;
  };

  // チェックが入っている端末が未登録端末可の確認　trueで未登録端末が選択されている
  const isSelectedUnconfirmedDeviceMulti = () => {
    if (!!tenantOption && !!deviceList) {
      const selectedDeviceType = new Set(deviceList.map((d: any) => !d.modelName ? "" : d.modelName));
      return selectedDeviceType.has("");
    }

    return false;
  }

  /***************************************************************
   * レイアウト
   **************************************************************/
  return (
    <>
      <Box
        display={"flex"}
        flexDirection={"column"}
        style={{ height: "calc(100vh - 64px)", width: "calc(100vw - 200px)" }}
      >
        {/* ボタンエリア */}
        <ButtonArea pageName={PAGE_NAME} isBackBtn={true} isEdit={true}>
          {/* <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit(handleEditBtn)}
          >
            保存
          </Button> */}

          {/* 削除ボタン */}
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAlertOpen}
          >
            端末削除
          </Button>
        </ButtonArea>

        {/* 入力フォーム */}
        <Box sx={{ p: 4 }} flex={"1"} style={{ overflowY: "scroll" }}>
          <Box sx={{ py: 2, px: 8 }}>
            <Grid container spacing={2} alignItems="center">
              {/* IMEI */}
              <FormLabel label="IMEI" />
              <Grid item xs={10}>
                {device.imei}
              </Grid>
              {/* 利用者 */}
              <FormLabel label="端末利用者" />
              <Grid item xs={10}>
                {/* {canEditParameterByOtherTenant() ?  */}
                  {/* <FormControl fullWidth> */}
                    <Controller
                      control={control}
                      name="deviceUserId"
                      defaultValue=""
                      render={({ field }) => (
                        <>
                          <Select size="small" {...field} displayEmpty>
                            <MenuItem value="">変更しない</MenuItem>
                            <MenuItem value="-1">端末利用者のリンク削除</MenuItem>
                            {deviceUserOption
                              ? deviceUserOption.map((item) => (
                                <MenuItem key={item.key} value={item.key}>
                                  {item.value}
                                </MenuItem>
                              ))
                              : ""}
                          </Select>
                          {errors.deviceUserId && (
                            <FormHelperText error>
                              {errors.deviceUserId?.message}
                            </FormHelperText>
                          )}
                        </>
                      )}
                    />
                  {/* </FormControl> : "他テナントを選択しているため、操作できません。" */}
                {/* } */}
              </Grid>

              {/* タグ */}
              {/* <FormLabel label="タグ" /> */}
              {/* タグモード選択 */}
              {/* {(isSelectedMultiDevice() && canEditParameterByOtherTenant()) && 
                <FormPullDown 
                  items={tagModeOption()}
                  mandatory
                  {...register("tagMode")}
                  xs={2}
                />
              } */}
              {/* タグ選択欄 */}
              {/* <Grid item xs={(isSelectedMultiDevice() && canEditParameterByOtherTenant()) ? 8 : 10}>
                {canEditParameterByOtherTenant() ? 
                  <FormControl fullWidth>
                    <Controller
                      control={control}
                      name="selectTag"
                      render={({ field: { ref, onChange, ...field } }) => (
                        <>
                          <Autocomplete
                            {...field}
                            size="small"
                            fullWidth
                            multiple
                            options={tagOption ?? []}
                            getOptionLabel={(option) => option.value}
                            isOptionEqualToValue={(option, v) =>
                              option.key == v.key
                            }
                            onChange={(_, data) => setValue("selectTag", data)}
                            renderInput={(params) => {
                              return (
                                <TextField
                                  {...field}
                                  {...params}
                                  fullWidth
                                  inputRef={ref}
                                />
                              );
                            }}
                          />
                        </>
                      )}
                    />
                  </FormControl> : "他テナントを選択しているため、操作できません。"
                }
              </Grid> */}
              {/* テナント */}
              <FormLabel label="テナント" />
              {authSet["ALL_NONE_SUB-TENANT"] ? <>  
                  <Grid item xs={9}>
                    <FormControl fullWidth>
                      <Controller
                        control={control}
                        name="tenantId"
                        defaultValue={""}
                        render={({ field }) => (
                          <>
                            <Select size="small" {...field} displayEmpty>
                              <MenuItem value="">変更しない</MenuItem>
                              {tenantOption
                                ? tenantOption.map((item) => (
                                  <MenuItem key={item.key} value={item.key}>
                                    {item.value}
                                  </MenuItem>
                                ))
                                : ""}
                            </Select>
                            {errors.tenantId && (
                              <FormHelperText error>
                                {errors.tenantId?.message}
                              </FormHelperText>
                            )}
                          </>
                        )}
                      />

                    </FormControl>
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton onClick={handleDiagramDialogOpen}>
                      <AccountTree />
                    </IconButton>
                  </Grid>
                </> : <>
                  <Grid item xs={10}>
                    {(!!device.tenantId && !!tenantOption) && valueFromKey(tenantOption, device.tenantId)}
                  </Grid>
                </>
              }

              {/* 入力欄：MDM無効化MDM有効化 */}
              <Grid item xs={2}>
                <Typography noWrap component="div" align="right">
                  MDM
                </Typography>
              </Grid>
              <Grid item xs={10}>
                {(device.modelName != null || (isSelectedMultiDevice() && !isSelectedUnconfirmedDeviceMulti())) ?
                  device.modelName ?
                    <Controller
                      control={control}
                      name="isMdmManaged"
                      defaultValue={getValues("isMdmManaged")}
                      render={({ field }) => (
                        <Switch
                          onChange={e => field.onChange(e.target.checked)}
                          checked={field.value}
                        />
                      )}
                    />
                    : isSelectedMultiDevice() ? 
                      <FormPullDown 
                        xs={12}
                        name="isMdmManaged"
                        items={isMdmOption()}
                        handle={handleBatchEdit}
                        defaultValue={getValues("isMdmManaged")?.toString()}
                        isDisplayEmpty
                        emptyItemLabel="変更しない"
                      /> 
                      : ""
                  : "登録確認未実施端末を選択しているため、変更できません。"
                }
              </Grid>
              
              {/* コメント */}
              {/* <FormLabel label="コメント" /> */}
              {/* コメント編集選択 */}
              {/* {(isSelectedMultiDevice() && canEditParameterByOtherTenant()) && 
                <FormPullDown 
                  items={commentModeOption()}
                  mandatory 
                  {...register("commentMode")}
                  xs={2}
                />
              }          */}
              {/* コメント入力欄 */}
              {/* <Grid item xs={(isSelectedMultiDevice() && canEditParameterByOtherTenant()) ? 8 : 10}>
                {canEditParameterByOtherTenant() ? 
                  <TextField
                    disabled={watch("commentMode") === "0"}
                    fullWidth
                    size="small"
                    defaultValue={getValues("comment")}
                    type="comment"
                    {...register("comment")}
                    error={"comment" in errors}
                    helperText={errors.comment?.message}
                  /> : "他テナントを選択しているため、操作できません。"
                }
              </Grid> */}
            </Grid>
          </Box>
        </Box>
      </Box>

      {/* テナントの選択ダイアログ */}
      {/* <TenantDiagramDialog 
        isDiagramDialogOpen={isDiagramDialogOpen} 
        selectedTenantIdList={[+watch("tenantId")]}
        handleDialogClose={handleDiagramDialogClose} 
        handleBoxClick={(ids) => {
          setValue("tenantId", (ids.length > 0) ? ids[0].toString() : "");
        }}
      /> */}

      {/* 編集確認ダイアログ */}
      <AlertDialog
        isAlertDialogOpen={isEditAlertDialogOpen}
        title="保存確認"
        message="端末の情報"
        mode="EDIT"
        handleAlertCancel={handleEditAlertClose}
        handleAlertOk={handleEditConfirmBtn}
      />

      {/* 削除確認ダイアログ */}
      <AlertDialog
        isAlertDialogOpen={isDeleteAlertDialogOpen}
        title="削除確認"
        message={"IMEI: " + device.imei + " を削除しますか？"}
        mode="DELETE"
        handleAlertCancel={handleDeleteAlertClose}
        handleAlertOk={handleDeleteBtn}
      />

      {/* 処理結果ダイアログ */}
      <ResultDialog
        isDialogOpen={isDialogOpen}
        resultResponse={resultResponse}
        handler={handleResultDialogClose}
      />

      {/* 処理ローディング */}
      <Loading isLoading={loading} />
    </>
  );
};

export default TerminalEdit;
